/**
 * useIsMobile — hook centralizado pra detecção de viewport mobile.
 *
 * Retorna `true` quando a viewport é <= breakpoint `sm` do MUI (599.95px).
 *
 * Por que existe:
 *   Antes desse hook, a detecção de mobile estava espalhada pela tela /2talk-chat
 *   em 3 formatos diferentes que conviviam mal:
 *     1. `useState(window.innerWidth <= 599.9)` + listener manual de resize
 *        — não acompanha mudanças de DPR e custa um listener por componente
 *     2. `useMediaQuery(theme.breakpoints.down('sm'))` em alguns lugares
 *        — boa prática, mas exigia injetar useTheme em cada componente
 *     3. `@media (max-width: 599.9px)` em makeStyles / .css
 *        — frágil porque o cutoff 599.9 não bate exatamente com o do MUI (599.95)
 *
 *   Este hook unifica tudo isso num único ponto, usa o MUI como source-of-truth
 *   e elimina o desalinhamento de 0.05px entre código JS e CSS.
 *
 * Por que `down('sm')` e não outro:
 *   O Material Design 3 define o breakpoint "compact" em <600px, que é exatamente
 *   o `sm` do MUI. Smartphones em portrait sempre caem aí; tablets em portrait
 *   ficam em `md` (que tratamos como "não-mobile" — layout desktop diminuído).
 *   Manter essa fronteira evita dar comportamento mobile pra tablets, que é o
 *   erro mais comum desse tipo de hook.
 *
 * Notas de implementação:
 *   - `useMediaQuery` com callback `(theme) => ...` evita importar `useTheme`
 *     explicitamente em cada caller — o próprio hook já injeta o theme do contexto.
 *   - Pode ser usado tanto em componentes desktop-first (para "esconder" áreas
 *     pesadas em mobile) quanto mobile-first (para mostrar drawers temporais).
 *   - SSR-safe: durante SSR retorna `false` (default do useMediaQuery).
 *
 * @returns {boolean} true quando viewport <= 599.95px
 *
 * @example
 *   const isMobile = useIsMobile();
 *   return <Drawer variant={isMobile ? 'temporary' : 'permanent'} ... />;
 */

import { useMediaQuery } from '@mui/material';

export default function useIsMobile() {
  return useMediaQuery((theme) => theme.breakpoints.down('sm'));
}
